/**
 * SleepPatternCircle
 *
 * Circular 24h sleep pattern visualization showing predicted daily rhythm.
 * 0:00 at the top, clockwise. 12:00 at the bottom.
 *
 * Three layers:
 *   1. Base: full circle ring in white/10
 *   2. Progress: pp-orange arc from 0:00 up to current time
 *   3. Sleep phases: cluster-based segments on top, dark-glass background
 *
 * Segments are positioned based on actual tracked sleep cluster data.
 * Night sleep respects quiet hours (shown as one segment).
 * Time labels at sleep phase boundaries in white/25.
 * Gaps between segments, minimum segment arc enforced.
 */

import { useMemo } from 'react';
import type { SleepPatternCluster } from '../hooks/useSleepPlan';

interface SleepPatternCircleProps {
  clusters: SleepPatternCluster[];
  quietHoursStart?: string; // "22:00"
  quietHoursEnd?: string;   // "06:00"
  disabled?: boolean;
}

interface SleepSegment {
  startMin: number;
  endMin: number;
  sleepType: 'nap' | 'night';
}

const TOTAL_MINUTES = 1440;
const GAP_DEG = 4;

function timeStringToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

function minutesToAngle(minutes: number): number {
  return ((minutes % TOTAL_MINUTES) / TOTAL_MINUTES) * 360;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  if (Math.abs(endAngle - startAngle) < 0.5) return '';
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

/** Thick arc with rounded end caps as a filled shape */
function describeThickArcRounded(
  cx: number, cy: number, rOuter: number, rInner: number, _rMid: number,
  startAngle: number, endAngle: number,
): string {
  if (Math.abs(endAngle - startAngle) < 0.5) return '';
  const halfThickness = (rOuter - rInner) / 2;
  const outerStart = polarToCartesian(cx, cy, rOuter, startAngle);
  const outerEnd = polarToCartesian(cx, cy, rOuter, endAngle);
  const innerStart = polarToCartesian(cx, cy, rInner, startAngle);
  const innerEnd = polarToCartesian(cx, cy, rInner, endAngle);
  const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `A ${halfThickness} ${halfThickness} 0 0 1 ${innerEnd.x} ${innerEnd.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    `A ${halfThickness} ${halfThickness} 0 0 1 ${outerStart.x} ${outerStart.y}`,
    'Z',
  ].join(' ');
}

function formatTime(minutes: number): string {
  const m = ((minutes % TOTAL_MINUTES) + TOTAL_MINUTES) % TOTAL_MINUTES;
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return `${h}:${String(min).padStart(2, '0')}`;
}

export function SleepPatternCircle({
  clusters,
  quietHoursStart = '22:00',
  quietHoursEnd = '06:00',
  disabled = false,
}: SleepPatternCircleProps) {

  const padding = 36;
  const innerSize = 180;
  const size = innerSize + padding * 2;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 72;
  const strokeWidth = 16;

  // Sleep segments: 24px wide, centered on the same radius as the base ring
  const sleepStroke = 24;
  const sleepStrokeHalf = sleepStroke / 2;
  const sleepOuterR = radius + sleepStrokeHalf;
  const sleepInnerR = radius - sleepStrokeHalf;

  // Minimum arc: arc length = sleepStroke (24px) -> forms a 1:1 circle when at minimum
  const minArcDeg = (sleepStroke / (2 * Math.PI * radius)) * 360;

  // Build sleep segments from cluster data
  // Filters: no phases during quiet hours (rest time), no phases >8h,
  // last phase before quiet hours may extend into it, morning phases only after quiet end
  const sleepSegments = useMemo((): SleepSegment[] => {
    if (!clusters || clusters.length === 0) return [];

    const quietStart = timeStringToMinutes(quietHoursStart);
    const quietEnd = timeStringToMinutes(quietHoursEnd);
    const MAX_PHASE_MINUTES = 8 * 60;

    // Helper: is a given minute inside the quiet window?
    const isInQuiet = (min: number): boolean => {
      const m = ((min % TOTAL_MINUTES) + TOTAL_MINUTES) % TOTAL_MINUTES;
      if (quietStart > quietEnd) {
        // Wraps midnight (e.g. 22:00–06:00)
        return m >= quietStart || m < quietEnd;
      }
      return m >= quietStart && m < quietEnd;
    };

    const segments: SleepSegment[] = [];

    // Night clusters: show as one block spanning quiet hours (this IS the rest time display)
    const nightClusters = clusters.filter(c => c.sleepType === 'night');
    if (nightClusters.length > 0) {
      if (quietStart > quietEnd) {
        segments.push({ startMin: quietStart, endMin: TOTAL_MINUTES + quietEnd, sleepType: 'night' });
      } else {
        segments.push({ startMin: quietStart, endMin: quietEnd, sleepType: 'night' });
      }
    }

    // Naps: filter by quiet hours and duration
    const napClusters = clusters.filter(c => c.sleepType === 'nap');
    for (const cluster of napClusters) {
      let endMin = cluster.avgEndMinutes;
      if (endMin < cluster.avgStartMinutes) endMin += TOTAL_MINUTES;

      // Skip phases longer than 8 hours
      if ((endMin - cluster.avgStartMinutes) > MAX_PHASE_MINUTES) continue;

      const startNorm = cluster.avgStartMinutes % TOTAL_MINUTES;

      // Skip if the nap STARTS inside quiet hours
      if (isInQuiet(startNorm)) continue;

      // Allow a nap that started before quiet hours to extend into them (clamp end)
      // but don't start new phases during quiet time
      segments.push({
        startMin: cluster.avgStartMinutes,
        endMin,
        sleepType: 'nap',
      });
    }

    return segments;
  }, [clusters, quietHoursStart, quietHoursEnd]);

  // Current time
  const now = new Date();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const currentAngle = minutesToAngle(currentMin);

  // Time labels at sleep phase boundaries
  const timeLabels = useMemo(() => {
    const labels: Array<{ minute: number; angle: number }> = [];
    const seen = new Set<number>();

    for (const seg of sleepSegments) {
      const startNorm = seg.startMin % TOTAL_MINUTES;
      const endNorm = seg.endMin % TOTAL_MINUTES;

      if (!seen.has(startNorm)) {
        seen.add(startNorm);
        labels.push({ minute: startNorm, angle: minutesToAngle(startNorm) });
      }
      if (!seen.has(endNorm)) {
        seen.add(endNorm);
        labels.push({ minute: endNorm, angle: minutesToAngle(endNorm) });
      }
    }
    return labels;
  }, [sleepSegments]);

  // Progress arc: 0:00 -> current time
  const progressArcPath = useMemo(() => {
    if (currentAngle < 1) return '';
    return describeArc(cx, cy, radius, 0, currentAngle);
  }, [cx, cy, currentAngle]);

  const sleepFill = 'rgba(15, 25, 35, 0.6)'; // dark-glass background
  const sleepBorder = 'rgba(255, 255, 255, 0.15)'; // dark-glass border

  // Cap trimming for wrap-around segments at midnight
  const capDeg = (sleepStrokeHalf / (2 * Math.PI * radius)) * 360;

  function renderSleepSegment(seg: SleepSegment, key: string) {
    const gap = GAP_DEG / 2;

    if (seg.endMin > TOTAL_MINUTES) {
      // Wrap-around: two arcs, trimmed at midnight so rounded caps don't overlap
      let startDeg1 = minutesToAngle(seg.startMin) + gap;
      const endDeg1 = 360 - capDeg;
      const startDeg2 = capDeg;
      let endDeg2 = minutesToAngle(seg.endMin % TOTAL_MINUTES) - gap;

      if (endDeg1 - startDeg1 < minArcDeg) startDeg1 = endDeg1 - minArcDeg;
      if (endDeg2 - startDeg2 < minArcDeg) endDeg2 = startDeg2 + minArcDeg;

      const p1 = describeThickArcRounded(cx, cy, sleepOuterR, sleepInnerR, radius, startDeg1, endDeg1);
      const p2 = describeThickArcRounded(cx, cy, sleepOuterR, sleepInnerR, radius, startDeg2, endDeg2);
      return (
        <g key={key}>
          {p1 && <path d={p1} fill={sleepFill} stroke={sleepBorder} strokeWidth={1} strokeDasharray="3 2" />}
          {p2 && <path d={p2} fill={sleepFill} stroke={sleepBorder} strokeWidth={1} strokeDasharray="3 2" />}
        </g>
      );
    }

    let startDeg = minutesToAngle(seg.startMin) + gap;
    let endDeg = minutesToAngle(seg.endMin) - gap;

    const arcDeg = endDeg - startDeg;
    if (arcDeg < minArcDeg) {
      const center = (startDeg + endDeg) / 2;
      startDeg = center - minArcDeg / 2;
      endDeg = center + minArcDeg / 2;
    }

    if (endDeg <= startDeg) return null;
    const path = describeThickArcRounded(cx, cy, sleepOuterR, sleepInnerR, radius, startDeg, endDeg);
    if (!path) return null;
    return <path key={key} d={path} fill={sleepFill} stroke={sleepBorder} strokeWidth={1} strokeDasharray="3 2" />;
  }

  return (
    <div className="flex flex-col items-center">
      <div style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          overflow="visible"
        >
          {/* Layer 1: Base circle ring — white/10 */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="rgba(255, 255, 255, 0.10)"
            strokeWidth={strokeWidth}
          />

          {/* Layer 2: Progress — pp-orange from 0:00 to current time */}
          {!disabled && progressArcPath && (
            <path
              d={progressArcPath}
              fill="none"
              stroke="rgba(245, 158, 17, 0.75)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          )}

          {/* Layer 3: Sleep phase segments — dark-glass */}
          {!disabled && sleepSegments.map((seg, i) => renderSleepSegment(seg, `sleep-${i}`))}

          {/* Time labels at sleep phase boundaries */}
          {!disabled && timeLabels.map((label, i) => {
            const labelR = sleepOuterR + 10;
            const pos = polarToCartesian(cx, cy, labelR, label.angle);
            const isRight = pos.x > cx + 8;
            const isLeft = pos.x < cx - 8;
            const anchor = isRight ? 'start' : isLeft ? 'end' : 'middle';
            return (
              <text
                key={i}
                x={pos.x}
                y={pos.y}
                textAnchor={anchor}
                dominantBaseline="central"
                fill="rgba(255, 255, 255, 0.25)"
                fontSize="10"
                fontWeight="400"
              >
                {formatTime(label.minute)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
